import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import useFetch from "../../../../hooks/useFetch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faPlus,
  faEdit,
  faDownload,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
// import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { PDFDownloadLink } from "@react-pdf/renderer";

import { usePDActiveSubscription } from "../../hooks/pdActiveSubscription";

import SHGPDF from "./SHGPDF";
import SHGCreateEditModal from "./SHGCreateEditModal";
import SHGAddMemberModal from "./SHGAddMemberModal";
import PDBuySubscription from "../../PDBuySubscription";

const FoundHerSHG = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const token = authState?.token;
  const [fetchData] = useFetch();

  const user = authState?.user;
  const userId = authState?.user?._id;
  const userCountry = authState?.user?.country;
  const isCountryMissing = !userCountry;
  const userState = authState?.user?.state;
  const isStateMissing = !userState;

  const [shgs, setShgs] = useState([]);
  const [selectedSHG, setSelectedSHG] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("my-shg");
  const [isActive, setIsActive] = useState(false);
  const [message, setMessage] = useState("");
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showPDSubscription, setShowPDSubscription] = useState(false);

  const [formData, setFormData] = useState({
    groupName: "",
    groupMotto: "",
    groupDesc: "",
    area: authState?.user?.area,
    city: authState?.user?.city,
    state: authState?.user?.state,
    country: authState?.user?.country,
    pincode: authState?.user?.pinCode,
    address: "",
    groupMobile: authState?.user?.mobile,
    groupWhatsApp: authState?.user?.mobile,
    groupEmail: authState?.user?.email,
    splPublicMessage: "",
  });

  const [newMember, setNewMember] = useState({
    userCode: "",
    userName: "",
    contributionAmount: 0,
    joiningDate: new Date(),
    isActive: true,
    memberKRA: "",
  });

  const {
    subscriptions,
    selectedSubscription,
    loadingSubs,
    product,
    isActive: subscriptionActive,
    daysLeft,
    formatRange,
    refetch,
  } = usePDActiveSubscription();

  useEffect(() => {
    if (subscriptionActive !== isActive) {
      setIsActive(subscriptionActive);
    }
  }, [subscriptionActive, isActive]);

  // Fetch My SHGs
  const fetchMySHGs = async () => {
    setLoading(true);
    try {
      const requestConfig = {
        url: "/foundher/my-shgs",
        method: "post",
        data: { userId },
        headers: { Authorization: token },
      };

      const response = await fetchData(requestConfig, { silent: true });

      if (response?.status) {
        setShgs(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching SHGs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "my-shg" && isActive && token && user?._id) {
      fetchMySHGs();
    }
  }, [activeTab, isActive, token, user?._id]);

  // ================== CREATE / UPDATE / ADD MEMBER ==================

  const createNewSHG = async () => {
    if (!formData.groupName) {
      setMessage("Group Name is required");
      return;
    }
    if (submitting) return;

    if (!isActive) {
      setShowCreateForm(false);
      setShowSubscribeModal(true);
      return;
    }

    setSubmitting(true);

    try {
      const requestConfig = {
        url: "/foundher/create",
        method: "post",
        data: formData,
        headers: { Authorization: token },
      };

      const response = await fetchData(requestConfig, { silent: true });

      if (response?.status) {
        setShgs((prev) => [...prev, response.data]);
        setSelectedSHG(response.data);
        setShowCreateForm(false);
        resetFormData();
        flashMessage("Congratulation!! Your dream SHG created Successfully.");
      } else {
        setMessage("Failed to create group");
      }
    } catch (error) {
      console.error(error);
      setMessage("Error creating group");
    } finally {
      setSubmitting(false);
    }
  };

  const updateExistingSHG = async () => {
    if (!selectedSHG?._id) return;

    try {
      const requestConfig = {
        url: `/foundher/${selectedSHG._id}`,
        method: "put",
        data: formData,
        headers: { Authorization: token },
      };

      const response = await fetchData(requestConfig, { silent: true });

      if (response?.status) {
        setSelectedSHG(response.data);
        setShowCreateForm(false);
        setIsEditing(false);
        fetchMySHGs();
        flashMessage("Group updated successfully ✨");
      } else {
        setMessage("Failed to update group");
      }
    } catch (error) {
      setMessage("Failed to update group");
    }
  };

  const handleAddMember = async () => {
    if (!newMember.userName || !newMember.userCode) {
      setMessage("Name and User Code are required");
      return;
    }
    if (submitting) return;
    setSubmitting(true);

    try {
      const requestConfig = {
        url: `/foundher/${selectedSHG._id}/members`,
        method: "post",
        data: newMember,
        headers: { Authorization: token },
      };

      const response = await fetchData(requestConfig, { silent: true });

      if (response?.status) {
        setSelectedSHG(response.data);
        setShowAddMember(false);
        setNewMember({
          userCode: "",
          userName: "",
          contributionAmount: 0,
          joiningDate: new Date(),
          isActive: true,
          memberKRA: "",
        });
        fetchMySHGs();
        flashMessage("Member added successfully 💕");
      } else {
        setMessage("Failed to add member");
      }
    } catch (error) {
      setMessage("Failed to add member");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateMember = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const requestConfig = {
        url: `/foundher/${selectedSHG._id}/members/${editingMember._id}`,
        method: "put",
        data: newMember,
        headers: {
          Authorization: token,
        },
      };

      const response = await fetchData(requestConfig, { silent: true });

      if (response?.status) {
        setSelectedSHG(response.data);
        setShowAddMember(false);
        setEditingMember(null);

        setNewMember({
          userCode: "",
          userName: "",
          contributionAmount: 0,
          joiningDate: new Date(),
          isActive: true,
          memberKRA: "",
        });

        fetchMySHGs();
        flashMessage("Member updated successfully ✨");
      }
    } catch (e) {
      setMessage("Failed to update member");
    } finally {
      setSubmitting(false);
    }
  };

  const flashMessage = (text, duration = 3000) => {
    setMessage(text);
    setTimeout(() => setMessage(""), duration);
  };

  const resetFormData = () => {
    setFormData({
      groupName: "",
      groupMotto: "",
      groupDesc: "",
      area: authState?.user?.area || "",
      city: authState?.user?.city || "",
      state: authState?.user?.state || "",
      country: authState?.user?.country || "",
      pincode: authState?.user?.pinCode || "",
      address: "",
      groupMobile: authState?.user?.mobile || "",
      groupWhatsApp: authState?.user?.mobile || "",
      groupEmail: authState?.user?.email || "",
      splPublicMessage: "",
    });
  };

  if (loadingSubs) return <p>Loading subscription...</p>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-pink-950 to-gray-950 text-white font-sans">
      <div className="sticky top-0 bg-black/80 backdrop-blur-md z-50 border-b border-pink-500/30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-start justify-start border-b border-cyan-200 rounded-2xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center text-xl lg:text-2xl">
              🌸
            </div>
            <h1 className="text-xl lg:text-3xl font-bold tracking-tight">
              FoundHer <span className="text-pink-400">SHG</span>
            </h1>
          </div>
          {message && <div className="text-amber-400 text-sm">{message}</div>}
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-0.5 border-b border-pink-900 bg-black/40 mb-2">
          {[
            { label: "My SHG", key: "my-shg", icon: faUsers },
            {
              label: "Smart ToDo",
              key: "purpledor",
              route: "/purpledoordashboard",
            },
            {
              label: "DigitalBoutique",
              key: "digitalboutique",
              route: "/digitalboutiquedashboard",
            },
            {
              label: "My & Team Pulse",
              key: "pulse",
              route: "/pulsedashboard",
            },
            {
              label: "My LifeReplays",
              key: "lifereplays",
              route: "/lifereplaydashboard",
            },
            {
              label: "ScorePlus",
              key: "scoreplus",
              route: "/scoreplus",
            },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                if (tab.key === "my-shg") {
                  setActiveTab("my-shg");
                  return;
                }
                navigate(tab.route);
              }}
              className={`w-fit px-2 py-1 leading-none text-xs lg:text-sm font-medium whitespace-nowrap transition-all border-b-2 flex items-center gap-2 ${
                activeTab === tab.key
                  ? "border-pink-500 text-pink-400"
                  : "border-transparent hover:text-pink-300"
              }`}
            >
              {tab.icon && <FontAwesomeIcon icon={tab.icon} className="mr-1" />}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="w-full mx-auto px-4 py-2">
        {activeTab === "my-shg" && (
          <>
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-fit px-2 py-1 flex items-center gap-3 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 rounded-xl text-sm lg:text-lg font-semibold shadow-xl shadow-pink-500/30 transition-all active:scale-95"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" /> Create New
              Group
            </button>

            {/* <pre>{JSON.stringify(shgs, null, 2)}</pre> */}
            {/* SHG Cards */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {shgs.map((shg) => (
                <div
                  key={shg._id}
                  onClick={() =>
                    setSelectedSHG((prev) =>
                      prev?._id === shg._id ? null : shg
                    )
                  }
                  className="bg-gray-900/70 border border-pink-900/50 rounded-xl px-4 py-1 hover:border-pink-500 cursor-pointer group transition-all hover:-translate-y-1"
                >
                  {/* <pre>{JSON.stringify(shg, null, 2)}</pre>                  */}
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-sm lg:text-lg font-semibold text-pink-100">
                        {shg.groupName}
                      </h3>
                      <p className="text-pink-300/70 text-xs lg:text-sm mt-1">
                        {shg.groupMotto}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData({
                          groupName: shg.groupName || "",
                          groupMotto: shg.groupMotto || "",
                          groupDesc: shg.groupDesc || "",
                          area: shg.area || "",
                          city: shg.city || "",
                          state: shg.state || "",
                          pincode: shg.pincode || "",
                          address: shg.address || "",
                          groupMobile: shg.groupMobile || "",
                          groupWhatsApp: shg.groupWhatsApp || "",
                          groupEmail: shg.groupEmail || "",
                          splPublicMessage: shg.splPublicMessage || "",
                        });
                        setSelectedSHG(shg);
                        setIsEditing(true);
                        setShowCreateForm(true);
                      }}
                      className="text-pink-400 hover:text-pink-300 w-fit p-1"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  </div>
                  <div className="mt-2 w-fit px-2 py-1 flex items-center gap-2 text-sm text-gray-400">
                    <FontAwesomeIcon icon={faUsers} className="mr-1" />{" "}
                    {shg.membersCount} Members
                  </div>
                </div>
              ))}
            </div>

            {/* Selected Group Details */}
            {selectedSHG && (
              <div className="mt-6 bg-gray-900 border border-pink-800/50 rounded-xl px-4 py-2">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h2 className="text-lg lg:text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">
                      {selectedSHG.groupName}
                    </h2>
                    <p className="text-sm lg:text-lg text-pink-300 mt-2">
                      {selectedSHG.groupMotto}
                    </p>
                  </div>
                  <PDFDownloadLink
                    document={<SHGPDF data={selectedSHG} />}
                    fileName={`${selectedSHG.groupName}.pdf`}
                  >
                    {({ loading }) => (
                      <button className="block bg-emerald-600 hover:bg-emerald-500 w-full px-2 py-1 text-xs lg:text-sm rounded-md">
                        <FontAwesomeIcon icon={faDownload} className="mr-1" />{" "}
                        {loading ? "Preparing..." : "PDF"}
                      </button>
                    )}
                  </PDFDownloadLink>
                </div>

                {/* Group Info */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div>
                      <h4 className="uppercase tracking-widest text-pink-400 text-sm mb-2">
                        Our Story
                      </h4>
                      <p className="text-gray-300 leading-relaxed">
                        {selectedSHG.groupDesc || "No description available."}
                      </p>
                    </div>
                    <div>
                      <h4 className="uppercase tracking-widest text-pink-400 text-sm mb-2">
                        Contact &amp; Social
                      </h4>
                      <div className="flex flex-wrap gap-4 text-sm">
                        {selectedSHG.groupMobile && (
                          <div>📱 {selectedSHG.groupMobile}</div>
                        )}
                        {selectedSHG.groupWhatsApp && <div>💬 WhatsApp</div>}
                        {selectedSHG.groupEmail && (
                          <div>✉️ {selectedSHG.groupEmail}</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Members Section */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="uppercase tracking-widest text-pink-400 text-sm">
                        Members in Circle
                      </h4>
                      {selectedSHG.creatorId?.toString() ===
                        user?._id?.toString() && (
                        <button
                          onClick={() => setShowAddMember(true)}
                          className="text-pink-400 w-fit px-2 py-1 text-sm flex items-center gap-1 hover:underline"
                        >
                          <FontAwesomeIcon
                            icon={faPlus}
                            className="text-xs mr-1"
                          />{" "}
                          Add Member
                        </button>
                      )}
                    </div>
                    <div className="space-y-4">
                      {selectedSHG.groupMembers?.map((member, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between bg-gray-800/50 px-4 py-1 rounded-xl"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-pink-900 rounded-2xl flex items-center justify-center text-lg lg:text-xl">
                              👩🏻‍🤝‍👩🏾
                            </div>
                            <div>
                              <div className="font-medium">
                                {member.userName}
                              </div>
                              <div className="text-xs text-pink-300/70">
                                Code: {member.userCode}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right text-sm">
                              <div>₹{member.contributionAmount}</div>
                              <div className="text-emerald-400 text-xs">
                                {member.isActive ? "Active" : "Inactive"}
                              </div>
                            </div>

                            {selectedSHG.creatorId?.toString() ===
                              user?._id?.toString() && (
                              <button
                                onClick={() => {
                                  setEditingMember(member);
                                  setShowAddMember(true);
                                }}
                                className="w-fit px-1 py-1 text-blue-400 hover:text-blue-300 transition"
                                title="Edit Member"
                              >
                                <FontAwesomeIcon icon={faPenToSquare} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Registrations */}
                {selectedSHG.registrationNos?.length > 0 && (
                  <div className="mt-4">
                    <h4 className="uppercase tracking-widest text-pink-400 text-sm mb-2">
                      Legal Registrations
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedSHG.registrationNos.map((reg, i) => (
                        <div
                          key={i}
                          className="border border-pink-900/30 p-6 rounded-3xl"
                        >
                          <div className="text-lg lg:text-xl font-semibold">
                            {reg.type}
                          </div>
                          <div className="text-pink-300 text-sm lg:text-lg mt-2">
                            {reg.details}
                          </div>
                          <div className="text-xs text-gray-500 mt-2">
                            Valid till:{" "}
                            {reg.expiryDate
                              ? new Date(reg.expiryDate).toLocaleDateString()
                              : "N/A"}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <SHGCreateEditModal
        isOpen={showCreateForm}
        onClose={() => {
          setShowCreateForm(false);
          setIsEditing(false);
          setMessage("");
        }}
        formData={formData}
        setFormData={setFormData}
        onSubmit={isEditing ? updateExistingSHG : createNewSHG}
        isEditing={isEditing}
        message={message}
        submitting={submitting}
      />

      <SHGAddMemberModal
        isOpen={showAddMember}
        onClose={() => {
          setShowAddMember(false);
          setEditingMember(null);
          setNewMember({
            userCode: "",
            userName: "",
            contributionAmount: 0,
            joiningDate: new Date(),
            isActive: true,
            memberKRA: "",
          });
          setMessage("");
        }}
        newMember={newMember}
        setNewMember={setNewMember}
        editingMember={editingMember}
        onSubmit={editingMember ? handleUpdateMember : handleAddMember}
        message={message}
        submitting={submitting}
      />

      {/* Subscription Modal */}
      {showSubscribeModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-zinc-900 border border-pink-900 rounded-xl p-8 max-w-md w-full mx-4 text-center">
            <div className="text-5xl mb-4">🔒</div>
            <h3 className="text-2xl font-semibold text-white mb-3">
              Premium Feature
            </h3>
            <p className="text-zinc-400 mb-8">
              Buy FoundHer / Director Subscription to unlock Private SHG Management
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowSubscribeModal(false)}
                className="flex-1 w-fit py-1 px-2 border border-zinc-700 hover:bg-zinc-800 rounded-lg text-white transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  setShowSubscribeModal(false);
                  setShowPDSubscription(true);
                }}
                className="flex-1 w-fit py-1 px-2 bg-pink-600 hover:bg-pink-500 rounded-lg text-white font-medium transition-colors"
              >
                Subscribe Now
              </button>
            </div>
          </div>
        </div>
      )}
      {showPDSubscription && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] px-4 py-6 overflow-y-auto">
          <div className="bg-gray-900 border border-pink-500/50 rounded-3xl w-full max-w-2xl my-auto max-h-[90vh] overflow-y-auto">
            <div className="flex justify-end px-4 pt-4">
              <button
                onClick={() => {
                  setShowPDSubscription(false);
                  refetch && refetch();
                }}
                className="text-xs lg:text-sm w-fit px-2 py-1 text-gray-400 hover:text-white leading-none"
              >
                ✕
              </button>
            </div>
            <PDBuySubscription user={user}/>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoundHerSHG;
